/**
 * AITextGenerator — Component reutilitzable per generar textos amb IA.
 * Mostra un diàleg on l'usuari pot generar un esborrany, editar-lo i copiar/usar-lo.
 */
import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Copy, Check, RefreshCw } from "lucide-react";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onApply: (text: string) => void  — callback quan l'usuari accepta el text
 *  - title: string — títol del diàleg
 *  - prompt: string — prompt complet per enviar a la IA
 *  - placeholder: string — placeholder del textarea d'edició
 */
export default function AITextGenerator({ open, onClose, onApply, title, prompt, placeholder }) {
  const [generatedText, setGeneratedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const SYSTEM = `Ets un expert en gestió de restaurants i seguretat alimentària (normativa APPCC, Reglament CE 852/2004).
Genera textos professionals, clars i pràctics en català.
Retorna ÚNICAMENT el text sol·licitat, sense introducció ni explicació addicional.
El text ha de ser directament usable en un document oficial o registre intern.`;

      const fullPrompt = `${SYSTEM}\n\n${prompt}`;
      const response = await hosys.integrations.Core.InvokeLLM({ prompt: fullPrompt });
      setGeneratedText(typeof response === "string" ? response : JSON.stringify(response));
      setHasGenerated(true);
    } catch {
      setGeneratedText("No s'ha pogut generar el text. Torna-ho a intentar.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = () => {
    onApply(generatedText);
    onClose();
  };

  const handleClose = () => {
    setGeneratedText("");
    setHasGenerated(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#f9f0f2] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#7B2D3F]" />
            </div>
            {title || "Generar text amb IA"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!hasGenerated && !loading && (
            <div className="bg-[#f9f0f2] border border-[#7B2D3F]/10 rounded-xl p-4 text-center">
              <Sparkles className="w-8 h-8 text-[#7B2D3F] mx-auto mb-2 opacity-70" />
              <p className="text-sm text-gray-600 mb-1 font-medium">L'assistent generarà un esborrany</p>
              <p className="text-xs text-gray-400">Podràs editar el text abans d'aplicar-lo</p>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="w-8 h-8 text-[#7B2D3F] animate-spin" />
              <p className="text-sm text-gray-500">Generant text...</p>
            </div>
          )}

          {hasGenerated && !loading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Esborrany generat — editable</p>
                <div className="flex gap-1">
                  <button
                    onClick={generate}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Regenerar"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleCopy}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copiar"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <Textarea
                value={generatedText}
                onChange={e => setGeneratedText(e.target.value)}
                className="h-48 resize-none text-sm leading-relaxed"
                placeholder={placeholder || "El text generat apareixerà aquí..."}
              />
              <p className="text-[11px] text-gray-400">
                ✏️ Edita el text lliurement abans d'aplicar-lo
              </p>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={handleClose} size="sm">
              Cancel·lar
            </Button>
            {!hasGenerated ? (
              <Button
                onClick={generate}
                disabled={loading}
                size="sm"
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-1.5"
              >
                {loading
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generant...</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Generar</>
                }
              </Button>
            ) : (
              <Button
                onClick={handleApply}
                size="sm"
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-1.5"
              >
                <Check className="w-3.5 h-3.5" /> Aplicar al camp
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}